import { useQuery } from "@tanstack/react-query";
import { useLocation, useRouter } from "@tanstack/react-router";

import { shellBootstrapQuery } from "./bootstrap";
import { NavigationCards } from "./NavigationCards";
import type { ShellServices } from "./services";
import { EmptyState, ErrorPage, type ErrorStatus } from "./states";
import {
  siteContentNavigation,
  workspaceNavigation,
} from "./workspaceNavigation";

export function OverviewPage({ services }: { services: ShellServices }) {
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const items =
    bootstrap.data === undefined
      ? []
      : workspaceNavigation({
          permissions: bootstrap.data.permissions,
          modules: bootstrap.data.navigation,
        });

  return (
    <section className="workspace-page" aria-labelledby="overview-title">
      <div className="page-heading">
        <p className="eyebrow">Рабочее пространство</p>
        <h1 id="overview-title">Обзор ERP</h1>
        <p>
          Выберите рабочий модуль или контур управления, чтобы начать работу.
        </p>
      </div>
      <NavigationCards items={items} />
    </section>
  );
}

export function SiteContentOverviewPage({
  services,
}: {
  services: ShellServices;
}) {
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const items =
    bootstrap.data === undefined
      ? []
      : siteContentNavigation(bootstrap.data.permissions);

  return (
    <section className="workspace-page" aria-labelledby="site-overview-title">
      <div className="page-heading">
        <p className="eyebrow">Публичный сайт</p>
        <h1 id="site-overview-title">Управление сайтом</h1>
        <p>
          Выберите раздел для работы с содержимым, медиафайлами или настройками.
        </p>
      </div>
      <NavigationCards items={items} />
      {items.length === 0 ? (
        <EmptyState
          title="Нет доступных разделов"
          description="Для работы с содержимым сайта нужны дополнительные права."
        />
      ) : null}
    </section>
  );
}

export function ModulePlaceholder({ services }: { services: ShellServices }) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const item = bootstrap.data?.navigation.find(
    (candidate) =>
      candidate.path === pathname ||
      pathname.startsWith(`${candidate.path.replace(/\/$/, "")}/`),
  );

  if (item === undefined) {
    return <ErrorPage status={404} />;
  }

  return (
    <section className="workspace-page" aria-labelledby="module-title">
      <div className="page-heading">
        <p className="eyebrow">Модуль ERP</p>
        <h1 id="module-title">{item.label}</h1>
        <p>{item.module_key}</p>
      </div>
      <EmptyState
        title="Экран пока пуст"
        description="Маршрут защищён, а модуль доступен согласно текущим permissions. Бизнес-интерфейс будет добавлен следующей задачей."
      />
    </section>
  );
}

export function StaticErrorPage({ status }: { status: ErrorStatus }) {
  const router = useRouter();

  return (
    <ErrorPage
      status={status}
      onRetry={status === 503 ? () => void router.invalidate() : undefined}
    />
  );
}
